import React from 'react';
import moment from 'moment';
import { Rating } from 'react-custom-rating-component';
import { DAV_MAIN_DOMAIN } from '../../../constants';
import { formatCurrency } from '../../../utils';
import { Badge } from '../../ui';


export const bookingsColumns = [
  {
    Header: 'Name',
    accessor: 'user_name',
    Cell: ({ cell: { value } }) => (
      <div className='font-medium text-gray-900 dark:text-white'>{value}</div>
    ),
  },
  {
    Header: 'Tour / Schedule',
    accessor: 'tour.name',
    Cell: ({ row }) => {
      const { travelDate, refNo, tour } = row.original;
      
      return (
        <div className='flex flex-col gap-1'>
          <div className='font-semibold text-xs text-gray-900 dark:text-white line-clamp-1'>
            {tour?.name || 'N/A'}
          </div>
          {(travelDate || refNo) && (
            <div className='flex flex-wrap gap-1'>
              {travelDate && (
                <Badge variant='info' size='xs' className='text-[9px] uppercase font-bold py-0 h-4'>
                  {travelDate}
                </Badge>
              )}
              {refNo && (
                <div className='text-[10px] font-bold text-primary-600 dark:text-primary-400 tabular-nums'>
                  #{refNo}
                </div>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    Header: 'Email',
    accessor: 'email',
    Cell: ({ cell: { value } }) => (
      <a href={`mailto:${value}`} className='text-gray-500 hover:text-primary-500 transition-colors'>
        {value}
      </a>
    ),
  },
  {
    Header: 'Phone',
    accessor: 'phone',
    Cell: ({ cell: { value } }) => (
      <span className='text-gray-500 font-medium'>{value}</span>
    ),
  },
  {
    Header: 'Country',
    accessor: 'country_of_residence',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <Badge variant='neutral' size='sm' pill>{value}</Badge>
    ),
  },
  {
    Header: 'Travel Plans',
    accessor: 'travel_plans',
    style: {
      minWidth: '240px',
    },
    Cell: ({ cell: { value } }) => (
      <div className='whitespace-pre-wrap line-clamp-2 text-xs text-gray-500 italic'>
        {value}
      </div>
    ),
  },
  {
    Header: 'Date',
    accessor: 'createdAt',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <div className='text-xs tabular-nums text-gray-400'>
        {moment(value).format('MMM DD, YYYY')} • {moment(value).format('hh:mm A')}
      </div>
    ),
  },
];

export const reviewsColumns = [
  {
    Header: 'Name',
    accessor: 'user_name',
    Cell: ({ cell: { value } }) => (
      <div className='font-semibold text-gray-900 dark:text-white'>{value}</div>
    ),
  },
  {
    Header: 'Rating',
    accessor: 'rating',
    center: true,
    Cell: ({ cell: { value } }) => (
      <div className='flex justify-center'>
        <Rating defaultValue={value} readOnly size='16px' />
      </div>
    ),
  },
  {
    Header: 'Review',
    accessor: 'review',
    Cell: ({ cell: { value } }) => (
      <div className='whitespace-pre-wrap line-clamp-2 text-sm max-w-xs'>
        {value}
      </div>
    ),
  },
  {
    Header: 'Visit',
    accessor: 'visit_month',
    Cell: ({ row }) => (
      <Badge variant='info' size='sm'>
        {row.original.visit_month} {row.original.visit_year}
      </Badge>
    ),
  },
  {
    Header: 'Country',
    accessor: 'country_of_residence',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <Badge variant='neutral' size='sm'>{value}</Badge>
    ),
  },
  {
    Header: 'Date',
    accessor: 'createdAt',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <span className='text-xs text-gray-400 tabular-nums'>{moment(value).format('MMM DD, YYYY • hh:mm A')}</span>
    ),
  },
];

export const subscribersColumns = [
  {
    Header: 'Name',
    accessor: 'name',
    Cell: ({ cell: { value } }) => (
      <div className='font-semibold text-gray-900 dark:text-white'>{value}</div>
    ),
  },
  {
    Header: 'Email',
    accessor: 'email',
    Cell: ({ cell: { value } }) => (
      <a href={`mailto:${value}`} className='text-primary-600 hover:underline'>
        {value}
      </a>
    ),
  },
  {
    Header: 'Phone',
    accessor: 'phone',
  },
  {
    Header: 'Subscribed',
    accessor: 'createdAt',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <div className='text-xs text-gray-400 tabular-nums'>{moment(value).format('MMM DD, YYYY • hh:mm A')}</div>
    ),
  },
];

export const pagesColumns = [
  {
    Header: 'Image',
    accessor: 'SEOImage',
    center: true,
    Cell: ({ cell: { value } }) => (
      <div className='w-16 h-10 rounded-lg overflow-hidden border border-gray-100 dark:border-gray-800 shadow-sm'>
        <img src={value} alt='' className='w-full h-full object-cover' />
      </div>
    ),
  },
  {
    Header: 'Title',
    accessor: 'title',
    Cell: ({ cell: { value, row } }) => (
      <div className='flex flex-col'>
        <span className='font-semibold text-gray-900 dark:text-white truncate max-w-[200px]'>{value}</span>
        <span className='text-[10px] text-primary-500 font-medium truncate max-w-[200px]'>
          {DAV_MAIN_DOMAIN}/{row?.original?.slug}
        </span>
      </div>
    ),
  },
  {
    Header: 'Description',
    accessor: 'description',
    Cell: ({ cell: { value } }) => (
      <div className='line-clamp-2 text-xs text-gray-500 max-w-[300px]'>{value}</div>
    ),
  },
  {
    Header: 'Last Modified',
    accessor: 'updatedAt',
    Cell: ({ cell: { value } }) => (
      <div className='text-xs text-gray-400 tabular-nums'>
        {moment(value).format('Do MMM YYYY')}
      </div>
    ),
  },
];

export const safariEnquiriesColumns = [
  {
    Header: 'Guest',
    accessor: 'firstName',
    Cell: ({ cell: { value, row } }) => (
      <div className='flex flex-col'>
        <span className='font-medium text-gray-900 dark:text-white'>{value} {row?.original?.lastName}</span>
        <a href={`mailto:${row?.original?.email}`} className='text-[11px] text-primary-600 hover:underline opacity-80'>
          {row?.original?.email}
        </a>
      </div>
    ),
  },
  {
    Header: 'Phone',
    accessor: 'phone',
    Cell: ({ cell: { value } }) => (
      <a href={`tel:${value}`} className='text-sm text-gray-600 hover:text-primary-500 transition-colors'>
        {value}
      </a>
    ),
  },
  {
    Header: 'Budget',
    accessor: 'budget',
    Cell: ({ cell: { value } }) => (
      <span className='font-medium text-gray-900 dark:text-white tabular-nums'>{value}</span>
    ),
  },
  {
    Header: 'Ideal Safari',
    accessor: 'idealSafari',
    Cell: ({ cell: { value } }) => (
      <div className='text-xs bg-gray-50/50 dark:bg-gray-800/30 p-2 rounded-xl border border-gray-100 dark:border-gray-800/50 whitespace-pre-wrap'>
        {value}
      </div>
    ),
  },
  {
    Header: 'Date',
    accessor: 'createdAt',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <span className='text-xs text-gray-400 tabular-nums'>{moment(value).format('MMM DD, YYYY • hh:mm A')}</span>
    ),
  },
];

export const quoteRequestsColumns = [
  {
    Header: 'Ref / Date',
    accessor: 'createdAt',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value, row } }) => (
      <div className='flex items-center gap-2'>
        <span className='font-medium text-gray-900 dark:text-white tabular-nums'>#{row?.original?.refNo}</span>
        <span className='text-[10px] text-gray-400 tabular-nums'>{moment(value).format('MMM DD, YYYY • hh:mm A')}</span>
      </div>
    ),
  },
  {
    Header: 'Client',
    accessor: 'user_name',
    Cell: ({ cell: { value, row } }) => (
      <div className='flex flex-col'>
        <span className='font-medium text-sm text-gray-900 dark:text-white'>{value}</span>
        <span className='text-[10px] text-gray-500 opacity-60'>{row?.original?.email}</span>
      </div>
    ),
  },
  {
    Header: 'Costs',
    accessor: 'costs',
    Cell: ({ row }) => (
      <span className='font-medium text-gray-900 dark:text-white tabular-nums'>
        {formatCurrency(row?.original?.tour?.price)}
      </span>
    ),
  },
  {
    Header: 'Status',
    accessor: 'status',
    Cell: ({ cell: { value, row } }) => (
      <div className='flex flex-col gap-1 items-start'>
        {value === 'confirmed' ? (
          <Badge variant='success' size='sm' dot pill>Replied</Badge>
        ) : (
          <Badge variant='warning' size='sm' dot pill>Pending</Badge>
        )}
        {row?.original?.clientConfirmation && (
          <span className='text-[9px] font-semibold text-primary-500 uppercase tracking-tighter'>
            Client Replied
          </span>
        )}
      </div>
    ),
  },
  {
    Header: 'Booking',
    accessor: 'bookingStatus',
    Cell: ({ cell: { row } }) => (
      <div>
        {row?.original?.clientConfirmation && row?.original?.status === 'confirmed' ? (
          <Badge variant='primary' size='sm'>Booked</Badge>
        ) : (
          <Badge variant='error' size='sm' pill={false}>Open</Badge>
        )}
      </div>
    ),
  },
];

export const agentColumns = [
  {
    Header: 'Agent / Company',
    accessor: 'username',
    Cell: ({ cell: { value, row } }) => (
      <div className='flex flex-col'>
        <span className='font-bold text-gray-900 dark:text-white'>{value}</span>
        <span className='text-[10px] text-gray-500 font-medium uppercase tracking-wider'>
          {row?.original?.companyName || 'No Company'}
        </span>
      </div>
    ),
  },
  {
    Header: 'Email',
    accessor: 'email',
    Cell: ({ cell: { value } }) => (
      <a href={`mailto:${value}`} className='text-primary-600 hover:underline text-xs font-medium'>
        {value}
      </a>
    ),
  },
  {
    Header: 'Phone',
    accessor: 'phoneNumber',
    Cell: ({ cell: { value } }) => (
      <span className='text-gray-500 text-xs tabular-nums font-medium'>{value}</span>
    ),
  },
  {
    Header: 'Country',
    accessor: 'country',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <Badge variant='neutral' size='sm' pill>{value}</Badge>
    ),
  },
  {
    Header: 'Status',
    accessor: 'isActive',
    center: true,
    Cell: ({ cell: { value } }) => (
      <Badge 
        variant={value ? 'success' : 'error'} 
        size='xs' 
        dot 
        pill
        className='uppercase font-bold tracking-tighter'
      >
        {value ? 'Active' : 'Inactive'}
      </Badge>
    ),
  },
  {
    Header: 'Joined',
    accessor: 'createdAt',
    className: 'whitespace-nowrap',
    Cell: ({ cell: { value } }) => (
      <div className='text-[10px] text-gray-400 font-medium tabular-nums'>
        {moment(value).format('MMM DD, YYYY')}
      </div>
    ),
  },
];
